import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BookModel, Class, Lesson } from '@/types';

export interface FilterSpec {
  title: string;
  status: boolean | null;
  classId: number;
  lessonId: number;
}

interface BookStoreState {
  books: BookModel[];
  filteredBooks: BookModel[];
  classes: Class[];
  lessons: Lesson[];
  filterSpec: FilterSpec;
  book?: BookModel | {};
}

// ** Initial State
const initialState: BookStoreState = {
  books: [],
  filteredBooks: [],
  classes: [],
  lessons: [],
  filterSpec: {
    title: '',
    status: null,
    classId: 0,
    lessonId: 0,
  },
};

// ** Slice
export const bookListStoreSlice = createSlice({
  name: 'bookStore',
  initialState,
  reducers: {
    setFilterSpec: (state, action: PayloadAction<FilterSpec>) => {
      state.filterSpec = action.payload;
    },
    resetUserBooks: (state) => {
      state.classes = [];
      state.lessons = [];
      state.books = [];
    },
    setBooks: (
      state,
      action: PayloadAction<{
        books: BookModel[];
        classes: Class[];
        lessons: Lesson[];
      }>,
    ) => {
      state.classes = action.payload.classes;
      state.lessons = action.payload.lessons;
      state.books = action.payload.books;
    },
    updateBookStatus: (
      state,
      action: PayloadAction<{
        bookId: number;
        isDownloaded: boolean;
      }>,
    ) => {
      const { bookId, isDownloaded } = action.payload;

      // Kitap listesini güncelle
      state.books = state.books.map(
        (book: BookModel) =>
          book.id === bookId
            ? { ...book, isDownloaded } // İlgili kitabı güncelle
            : book, // Diğer kitapları olduğu gibi bırak
      );
      state.filteredBooks = state.filteredBooks.map(
        (book: BookModel) =>
          book.id === bookId
            ? { ...book, isDownloaded } // İlgili kitabı güncelle
            : book, // Diğer kitapları olduğu gibi bırak
      );
    },
    filterBooks: (state) => {
      const { title, status, classId, lessonId } = state.filterSpec;
      const filteredBooks = state.books?.filter(
        (book: BookModel) =>
          (!title || book.title.toLowerCase().includes(title.toLowerCase())) &&
          (!classId || book.classId === classId) &&
          (!lessonId || book.lessonId === lessonId),
      );
      // Sadece indirilmiş Kitaplar
      if (status === true) {
        const bookList: BookModel[] = [];
        const downloadedBooks = filteredBooks.filter(
          (x: BookModel) => x.isDownloaded === true && x.isGroup === false,
        );
        for (let index = 0; index < downloadedBooks.length; index++) {
          if (downloadedBooks[index].hasGroup === true) {
            const parent = filteredBooks.find(
              (x) => x.id === downloadedBooks[index].groupId,
            );

            if (parent) {
              if (bookList.indexOf(parent) === -1) {
                bookList.push(parent);
              }
            }
          } else {
            bookList.push(downloadedBooks[index]);
          }
        }
        state.filteredBooks = bookList;
      } else {
        state.filteredBooks = filteredBooks;
      }
    },
    setSelectedBook: (state, action: PayloadAction<number | null>) => {
      if (action.payload !== null) {
        const bookId = action.payload;
        const selectedBook = state.books.find((x) => x.id === bookId);
        state.book = selectedBook || {};
      } else {
        state.book = {};
      }
    },
  },
  extraReducers: () => {},
});

// ** Actions
export const {
  setFilterSpec,
  setBooks,
  updateBookStatus,
  filterBooks,
  resetUserBooks,
  setSelectedBook,
} = bookListStoreSlice.actions;

// ** Reducer
export default bookListStoreSlice.reducer;
